import { useEffect, useState } from 'react';
import type { Campana, Conversion, Destinatario, EstadoEnvio, PlantillaMarketing, ProblemaCupo, Resultados } from '../../api/ops/campanas';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { cifra, diaMes, diaYHora, importe } from '../../logica/fechasCortas';
import { usePaginacionCursor } from '../../logica/paginacion';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import type { PropsApartado } from './Campanas';
import { Editor } from './Editor';
import { franjaDe } from './lineas';
import { accionesDe, CANAL, canalEnFrase, ESTADO_CAMPANA, ESTADO_DESTINATARIO, fallidosYDescartados, lineaDestinatario, motivosDe, PAUSA, porcentaje, RUTA_PLAN, textoComprar, textoSegmento, type AccionCampana } from './reglas';
import { Cifra } from './Resumen';

/**
 * 12.3 Campaña en curso o programada (`getCampaign`, `?campana=`): Resumen · Resultados · Destinatarios, con la franja de
 * su estado y sus botones (`accionesDe`): programada (Editar · Desprogramar con «Deshacer» · Cancelar), enviando (Detener lo
 * que queda), pausada con su motivo (comprar lleva a Plan, G561; «Reanudar» solo por cupo, G746; canal o plantilla siguen
 * solas, G744, G762; Pasar a email = detener y duplicar por email), enviada, cancelada o detenida (Duplicar). Un borrador
 * abre el editor (12.2). Resultados (12.4): completos con `campanas.resultados.ver`; el Encargado, las cifras principales de
 * la campaña. El detalle por cliente (conversiones y destinatarios) solo con `campanas.destinatarios.ver` (G459).
 */
interface Props extends PropsApartado {
    campanaId: string;
    onCerrar: () => void;
    /** La campaña cambió (la lista vuelve a leer); con el texto de lo hecho. */
    onCambio: (texto?: string) => void;
    /** Se creó otra campaña (duplicar, pasar a email): abrirla. */
    onAbrirOtra: (id: string) => void;
}

type Ventanas = 'cancelar' | 'detener' | 'reanudar' | 'email' | null;

export function FichaCampana(props: Props) {
    const { ops, negocio, movil, soloLectura, membresia, navegar, campanaId, onCerrar, onCambio, onAbrirOtra } = props;
    const { puede } = usePermisos();
    const aviso = useAvisoBreve();
    const [c, setC] = useState<Campana | null>(null);
    const [version, setVersion] = useState<string | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [pestana, setPestana] = useState(0);
    const [ventana, setVentana] = useState<Ventanas>(null);
    const [ocupado, setOcupado] = useState(false);
    const [editar, setEditar] = useState(false);
    const [nombres, setNombres] = useState<Readonly<Record<string, string>>>({});
    const [etiquetas, setEtiquetas] = useState<Readonly<Record<string, string>>>({});
    const [plantillas, setPlantillas] = useState<PlantillaMarketing[]>([]);
    const desprogramar = useEnvioRetrasado();

    useEffect(() => {
        let vivo = true;
        ops.getCampaign(negocio, campanaId)
            .then((r) => {
                if (vivo) {
                    setC(r.datos);
                    setVersion(r.version);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, campanaId, intento]);

    useEffect(() => {
        let vivo = true;
        // Nombres de quien la creó o la envió, etiquetas del segmento y la plantilla: si no se pueden leer, sin ellos.
        ops.listMembers(negocio, { limit: 100 })
            .then((r) => {
                if (vivo) {
                    setNombres(Object.fromEntries(r.data.map((m) => [m.id, m.display_name])));
                }
            })
            .catch(() => undefined);
        ops.listTags(negocio, { limit: 100 })
            .then((r) => {
                if (vivo) {
                    setEtiquetas(Object.fromEntries(r.data.map((t) => [t.id, t.name])));
                }
            })
            .catch(() => undefined);
        ops.listMarketingTemplates(negocio, { limit: 100 })
            .then((r) => {
                if (vivo) {
                    setPlantillas(r.data);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio]);

    if (error !== null) {
        return (
            <PanelLateral abierto onCerrar={onCerrar} movil={movil} tamano="ficha" titulo="Campaña" eyebrow="Campaña">
                <EstadoError seccion="la campaña" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={movil} />
            </PanelLateral>
        );
    }
    if (c === null) {
        return (
            <PanelLateral abierto onCerrar={onCerrar} movil={movil} tamano="ficha" titulo="Campaña" eyebrow="Campaña">
                <EstadoCargando seccion="la campaña" />
            </PanelLateral>
        );
    }
    if ((c.status === 'draft' && puede('campanas.campanas.crear') && !soloLectura) || editar) {
        return (
            <Editor
                {...props}
                campana={{ datos: c, version }}
                onCerrar={(texto) => {
                    setEditar(false);
                    if (c.status === 'draft') {
                        onCerrar();
                    } else {
                        setIntento((i) => i + 1);
                    }
                    onCambio(texto);
                }}
            />
        );
    }

    const acciones = accionesDe(c, { puede, soloLectura, propietario: membresia?.is_owner === true });
    const hayResultados = ['sending', 'paused', 'sent', 'stopped'].includes(c.status);
    const veDetalle = puede('campanas.destinatarios.ver');
    const pestanas: { etiqueta: string; icono: NombreIcono }[] = [
        { etiqueta: 'Resumen', icono: 'file-text' },
        ...(hayResultados ? [{ etiqueta: 'Resultados', icono: 'chart-column' as const }] : []),
        ...(hayResultados && veDetalle ? [{ etiqueta: 'Destinatarios', icono: 'users' as const }] : []),
    ];
    const indice = Math.min(pestana, pestanas.length - 1);
    const est = ESTADO_CAMPANA[c.status];
    const quedan = Math.max(0, (c.frozen_recipients ?? 0) - (c.handed_count ?? 0));
    const accionPanel = acciones.includes('editar') ? { texto: 'Editar', icono: 'pencil' as const } : acciones.includes('duplicar') ? { texto: 'Duplicar', icono: 'copy' as const } : null;

    const recargarCon = (texto: string) => {
        setIntento((i) => i + 1);
        onCambio(texto);
    };
    const fallo = (e: unknown, reintentar: () => void) => {
        const ro = avisoBreveDeError(e);
        if (e instanceof ErrorApi && e.estado === 412) {
            aviso({ tipo: 'error', texto: 'Alguien ha cambiado la campaña: la volvemos a leer.' });
            setIntento((i) => i + 1);
            return;
        }
        aviso({ tipo: 'error', texto: ro ?? (e instanceof Error ? e.message : 'No se ha podido hacer.'), reintentar });
    };
    const hacer = (accion: () => Promise<unknown>, texto: string, despues?: (r: unknown) => void) => {
        setOcupado(true);
        accion()
            .then((r) => {
                setVentana(null);
                if (despues === undefined) {
                    recargarCon(texto);
                } else {
                    despues(r);
                }
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'conflict' && ventana === 'reanudar') {
                    const p = e.problema as ProblemaCupo;
                    setVentana(null);
                    aviso({ tipo: 'error', texto: p.blocking_reason === 'sends_exhausted' || p.blocking_reason === 'marketing_pack_exhausted' ? 'Todavía no hay saldo: la campaña sigue en pausa.' : (p.detail ?? p.title) });
                    setIntento((i) => i + 1);
                    return;
                }
                fallo(e, () => { hacer(accion, texto, despues); });
            })
            .finally(() => {
                setOcupado(false);
            });
    };
    const duplicar = () => {
        hacer(() => ops.duplicateCampaign(negocio, c.id), '', (r) => {
            onAbrirOtra((r as Campana).id);
        });
    };
    const pulsar = (a: AccionCampana) => {
        switch (a) {
            case 'editar':
                setEditar(true);
                return;
            case 'desprogramar':
                if (version === null) {
                    return;
                }
                desprogramar.programar({
                    texto: 'Desprogramada: vuelve a ser un borrador.',
                    donde: 'ficha',
                    enviar: () => {
                        ops.unscheduleCampaign(negocio, c.id, version).then(() => { recargarCon('Desprogramada: vuelve a ser un borrador.'); }).catch((e: unknown) => { fallo(e, () => { pulsar('desprogramar'); }); });
                    },
                });
                return;
            case 'cancelar':
                setVentana('cancelar');
                return;
            case 'detener':
                setVentana('detener');
                return;
            case 'reanudar':
                setVentana('reanudar');
                return;
            case 'pasar-a-email':
                setVentana('email');
                return;
            case 'comprar':
                navegar(RUTA_PLAN);
                return;
            case 'duplicar':
                duplicar();
                return;
            case 'seguir':
                setEditar(true);
                return;
        }
    };
    const BOTON: Readonly<Record<AccionCampana, { texto: string; icono: NombreIcono; tipo: 'principal' | 'secundario'; rojo?: boolean }>> = {
        editar: { texto: 'Editar', icono: 'pencil', tipo: 'secundario' },
        desprogramar: { texto: 'Desprogramar', icono: 'undo-2', tipo: 'secundario' },
        cancelar: { texto: 'Cancelar la campaña', icono: 'calendar-x', tipo: 'secundario', rojo: true },
        detener: { texto: 'Detener lo que queda', icono: 'circle-x', tipo: 'secundario', rojo: true },
        comprar: { texto: textoComprar(c.pause_reason), icono: 'shopping-cart', tipo: 'principal' },
        reanudar: { texto: 'Reanudar', icono: 'play', tipo: acciones.includes('comprar') ? 'secundario' : 'principal' },
        'pasar-a-email': { texto: 'Pasar a email', icono: 'mail', tipo: 'secundario' },
        duplicar: { texto: 'Duplicar', icono: 'copy', tipo: 'secundario' },
        seguir: { texto: 'Seguir con el borrador', icono: 'pencil', tipo: 'secundario' },
    };
    const franja = franjaDe(c, quedan);
    const botones = acciones.filter((a) => a !== 'duplicar');
    const plantilla = plantillas.find((p) => p.template_id === c.content?.meta_template_id);
    const nombre = (id: string | null | undefined) => (id == null ? null : (nombres[id] ?? 'Alguien del equipo'));
    const muestra = c.last_preview?.sample;

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={movil}
                tamano="ficha"
                titulo={c.name}
                eyebrow="Campaña"
                subtitulo={`${CANAL[c.channel].nombre} · ${textoSegmento(c.segment, etiquetas)}`}
                {...(est.chip === null ? {} : { chip: est.texto, chipEstado: est.chip })}
                icono={CANAL[c.channel].icono}
                accion={accionPanel?.texto ?? ''}
                accionIcono={accionPanel?.icono ?? 'check'}
                ocupado={ocupado && ventana === null}
                ocupadoTexto="Duplicando…"
                {...(accionPanel === null ? {} : { onAccion: () => { pulsar(accionPanel.texto === 'Editar' ? 'editar' : 'duplicar'); } })}
            >
                <div className="rv-cn-ficha">
                    {pestanas.length > 1 && <Segmentos etiqueta="Partes de la campaña" movil={movil} items={pestanas} valor={indice} onCambio={setPestana} />}
                    {indice === 0 && (
                        <>
                            <div className={`rv-cp-franja${c.status === 'paused' ? ' rv-cp-franja--pausada' : ''}`} role={c.status === 'paused' ? 'alert' : 'status'}>
                                <div className="rv-cp-franja__cab">
                                    <span className="rv-cp-franja__baldosa">
                                        <Icono nombre={franja.icono} />
                                    </span>
                                    <span className="rv-cp-franja__texto">
                                        <strong>{franja.titulo}</strong>
                                        <span>{franja.texto}</span>
                                    </span>
                                </div>
                                {(c.status === 'sending' || c.status === 'paused') && (c.frozen_recipients ?? 0) > 0 && (
                                    <div className="rv-cp-progreso">
                                        <span className="rv-cp-pista-barra" role="progressbar" aria-valuemin={0} aria-valuemax={c.frozen_recipients ?? 0} aria-valuenow={c.handed_count ?? 0} aria-label="Mensajes encargados">
                                            <span style={{ width: `${String(Math.round(((c.handed_count ?? 0) / (c.frozen_recipients ?? 1)) * 100))}%` }} />
                                        </span>
                                        <div>
                                            <span>{`${cifra(c.handed_count ?? 0)} de ${cifra(c.frozen_recipients ?? 0)} encargados`}</span>
                                            <span>{c.status === 'sending' ? `Faltan ${cifra(quedan)}` : `Esperan ${cifra(quedan)}`}</span>
                                        </div>
                                    </div>
                                )}
                                {botones.length > 0 && (
                                    <div className="rv-cp-franja__botones">
                                        {botones.map((a) => (
                                            <Boton key={a} tipo={BOTON[a].tipo} rojo={BOTON[a].rojo === true} icono={BOTON[a].icono} movil={movil} onClick={() => { pulsar(a); }}>
                                                {BOTON[a].texto}
                                            </Boton>
                                        ))}
                                    </div>
                                )}
                                {c.status === 'scheduled' && !puede('campanas.campanas.crear') && <small>Solo el propietario y los administradores pueden cambiarla.</small>}
                                {c.status === 'paused' && c.pause_reason != null && PAUSA[c.pause_reason].porCupo && puede('campanas.campanas.enviar') && membresia?.is_owner !== true && <small>Comprar paquetes es cosa del propietario: pídeselo a él.</small>}
                            </div>
                            {desprogramar.pendiente !== null && (
                                <Deshacer clave={desprogramar.pendiente.clave} texto={desprogramar.pendiente.texto} onDeshacer={desprogramar.deshacer} movil={movil} />
                            )}
                            <div className="rv-cn-hechos">
                                <Hecho k="Canal" v={CANAL[c.channel].nombre} sub={c.channel === 'email' ? 'No gasta envíos' : c.channel === 'whatsapp' ? 'Envíos del bloque y paquete de marketing' : 'Envíos del bloque'} />
                                <Hecho
                                    k="Mensaje"
                                    v={c.channel === 'whatsapp' ? (plantilla?.name ?? 'Sin plantilla') : c.channel === 'email' ? (c.content?.email_subject ?? 'Sin asunto') : 'Texto libre'}
                                    {...(c.channel === 'whatsapp' && plantilla !== undefined ? { sub: `Plantilla ${plantilla.name}` } : {})}
                                />
                                <Hecho k="A quién" v={textoSegmento(c.segment, etiquetas)} sub={`Solo con permiso para ofertas por ${canalEnFrase(c.channel)}`} />
                                <Hecho
                                    k={c.frozen_recipients != null ? 'Destinatarios' : 'Destinatarios ahora'}
                                    v={c.frozen_recipients != null ? cifra(c.frozen_recipients) : c.last_preview == null ? '—' : `~${cifra(c.last_preview.audience_size)}`}
                                    {...(c.frozen_recipients != null ? { sub: `${cifra(c.excluded_no_consent ?? 0)} fuera por no tener permiso` } : c.last_preview == null ? {} : { sub: `${cifra(c.last_preview.excluded_no_consent)} fuera por no tener permiso · se recalcula al salir` })}
                                />
                                <Hecho k="Creada" v={`${diaMes(c.created_at)}${nombre(c.created_by_member_id) === null ? '' : ` · ${nombre(c.created_by_member_id) ?? ''}`}`} />
                                {c.sent_at != null && <Hecho k="Enviada por" v={nombre(c.sent_by_member_id) ?? 'Alguien del equipo'} sub={diaYHora(c.sent_at)} />}
                            </div>
                            {muestra !== undefined && (
                                <div className="rv-cp-seccion">
                                    <h3>Así le llega</h3>
                                    <div className="rv-cp-muestra">
                                        {muestra.subject != null && <strong>{muestra.subject}</strong>}
                                        {muestra.body !== undefined && <span>{muestra.body}</span>}
                                        {muestra.unsubscribe_footer !== undefined && <small>{muestra.unsubscribe_footer}</small>}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    {indice === 1 && hayResultados && <ResultadosCampana {...props} campana={c} />}
                    {indice === 2 && hayResultados && veDetalle && <Destinatarios ops={ops} negocio={negocio} movil={movil} campana={c} />}
                </div>
            </PanelLateral>
            <Ventana
                abierta={ventana === 'cancelar' || ventana === 'detener'}
                titulo={ventana === 'cancelar' ? `Cancelar «${c.name}»` : 'Detener lo que queda'}
                texto={ventana === 'cancelar' ? `No sale el ${diaYHora(c.scheduled_at)} y no se envía nada. Si la quieres para otro día, mejor desprográmala.` : `Los ${cifra(quedan)} mensajes que faltan no se envían. Los ${cifra(c.handed_count ?? 0)} ya encargados no se pueden recuperar.`}
                accion={ventana === 'cancelar' ? 'Cancelar la campaña' : 'Detener'}
                accionIcono={ventana === 'cancelar' ? 'calendar-x' : 'circle-x'}
                cancelar="Volver"
                ocupado={ocupado}
                ocupadoTexto={ventana === 'cancelar' ? 'Cancelando…' : 'Deteniendo…'}
                movil={movil}
                onAccion={() => { hacer(() => ops.cancelCampaign(negocio, c.id), ventana === 'cancelar' ? 'Campaña cancelada: no se envía nada.' : 'Campaña detenida: lo que quedaba no sale.'); }}
                onCerrar={() => { setVentana(null); }}
            >
                {ventana === 'detener' && (
                    <dl className="rv-cp-coste">
                        <div>
                            <dt>Ya encargados</dt>
                            <dd>{cifra(c.handed_count ?? 0)}</dd>
                        </div>
                        <div>
                            <dt>No saldrán</dt>
                            <dd>{cifra(quedan)}</dd>
                        </div>
                    </dl>
                )}
            </Ventana>
            <Ventana
                abierta={ventana === 'reanudar'}
                titulo="Reanudar la campaña"
                texto={`Siguen saliendo los ${cifra(quedan)} mensajes que faltaban por ${canalEnFrase(c.channel)}. Cada uno se vuelve a comprobar antes de enviarse y gasta su envío. Si todavía no hay saldo, te lo decimos y sigue en pausa.`}
                accion="Reanudar"
                accionIcono="play"
                cancelar="Volver"
                ocupado={ocupado}
                ocupadoTexto="Reanudando…"
                movil={movil}
                onAccion={() => { hacer(() => ops.resumeCampaign(negocio, c.id), 'Campaña reanudada: sigue saliendo.'); }}
                onCerrar={() => { setVentana(null); }}
            />
            <Ventana
                abierta={ventana === 'email'}
                titulo="Pasar a email"
                texto={`Se detiene esta campaña (los ${cifra(quedan)} que faltan no salen por ${canalEnFrase(c.channel)}) y se crea un borrador por email con el mismo segmento. Irá solo a quien tiene permiso por email: las dos audiencias no se mezclan.`}
                accion="Pasar a email"
                accionIcono="mail"
                cancelar="Volver"
                ocupado={ocupado}
                ocupadoTexto="Creando el borrador…"
                movil={movil}
                onAccion={() => {
                    hacer(
                        async () => {
                            await ops.cancelCampaign(negocio, c.id);
                            return ops.duplicateCampaign(negocio, c.id, { channel: 'email' });
                        },
                        '',
                        (r) => { onAbrirOtra((r as Campana).id); },
                    );
                }}
                onCerrar={() => { setVentana(null); }}
            />
        </>
    );
}

function Hecho({ k, v, sub }: { k: string; v: string; sub?: string }) {
    return (
        <div className="rv-cn-hecho">
            <span>{k}</span>
            <strong>{v}</strong>
            {sub !== undefined && sub !== '' && <small>{sub}</small>}
        </div>
    );
}

// ---- 12.4 Resultados de una campaña ----

function ResultadosCampana({ ops, negocio, movil, campana: c }: PropsPantalla & { campana: Campana }) {
    const { puede } = usePermisos();
    const completos = puede('campanas.resultados.ver');
    const detalle = puede('campanas.destinatarios.ver');
    const [r, setR] = useState<Resultados | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [conversiones, setConversiones] = useState<Conversion[] | null>(null);
    const [mas, setMas] = useState(false);
    const nombres = useNombres(ops, negocio, conversiones?.map((x) => x.customer_id) ?? []);

    useEffect(() => {
        if (!completos) {
            return;
        }
        let vivo = true;
        ops.getCampaignResults(negocio, c.id)
            .then((x) => {
                if (vivo) {
                    setR(x);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, c.id, completos]);

    useEffect(() => {
        if (!detalle) {
            return;
        }
        let vivo = true;
        ops.listConversions(negocio, { campaign_id: c.id, limit: 5 })
            .then((x) => {
                if (vivo) {
                    setConversiones(x.data);
                    setMas(x.next_cursor !== null);
                }
            })
            .catch(() => {
                if (vivo) {
                    setConversiones([]);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, c.id, detalle]);

    if (error !== null) {
        return <EstadoError seccion="los resultados" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setError(null); }} movil={movil} />;
    }
    if (completos && r === null) {
        return <EstadoCargando seccion="los resultados" />;
    }
    // Sin `campanas.resultados.ver` (el Encargado): las cifras principales de la campaña.
    const h = c.results ?? {};
    const destinatarios = c.frozen_recipients ?? r?.recipients ?? h.recipients ?? 0;
    const enviados = r?.sent ?? null;
    const entregados = r === null ? (h.delivered ?? null) : r.delivered;
    const leidos = r === null ? (h.read ?? null) : (r.read ?? null);
    const conv = r?.conversions ?? h.conversions ?? 0;
    const importeConv = r?.conversions_amount ?? h.conversions_amount;
    const fd = r === null ? null : fallidosYDescartados(r);
    const motivos = r === null ? [] : motivosDe(r);
    const embudo = [
        { k: 'Destinatarios', n: destinatarios, sub: 'Clientes con permiso a los que iba' },
        { k: 'Enviados', n: enviados, sub: 'Salieron hacia el canal' },
        { k: 'Entregados', n: entregados, sub: 'Con acuse de entrega' },
        { k: 'Leídos', n: leidos, sub: 'Con acuse de lectura' },
    ].filter((x): x is { k: string; n: number; sub: string } => x.n != null);

    return (
        <div className="rv-cn-ficha">
            <div className="rv-cp-cifras">
                <Cifra titulo="Reservas y compras" valor={cifra(conv)} pie={`${importeConv === undefined ? '' : `${importe(importeConv)} · `}en los 7 días siguientes`} oscura />
                <Cifra titulo="Destinatarios" valor={cifra(destinatarios)} pie={`${cifra(c.excluded_no_consent ?? 0)} fuera sin permiso`} />
                <Cifra titulo="Entregados" valor={entregados == null ? '—' : cifra(entregados)} pie={entregados == null ? 'Telegram no avisa de la entrega' : enviados === null ? '' : `${porcentaje(entregados, enviados)} de los enviados`} />
                <Cifra titulo="Leídos" valor={leidos == null ? '—' : cifra(leidos)} pie={c.channel === 'email' ? 'En email no se miden' : leidos == null ? 'Telegram no lo dice' : `${porcentaje(leidos, entregados)} de los entregados`} />
                {fd !== null && <Cifra titulo="Fallidos" valor={cifra(fd.fallidos)} pie={fd.descartados > 0 ? `Y ${cifra(fd.descartados)} que no salieron` : 'Ninguno más'} />}
                <Cifra titulo="Bajas" valor={cifra(r?.unsubscribes ?? h.unsubscribes ?? 0)} pie="Con su enlace o con BAJA en 7 días · nunca quién" />
            </div>
            {c.channel !== 'whatsapp' && <p className="rv-ca-nota"><Icono nombre="info" /><span>{c.channel === 'email' ? 'En email no medimos las aperturas: no es fiable y no respeta la privacidad.' : 'Telegram no avisa de la entrega ni de la lectura.'}</span></p>}
            {embudo.length > 1 && (
                <div className="rv-cp-barras">
                    <h3>Del envío a la lectura</h3>
                    <p>% de los destinatarios</p>
                    {embudo.map((x) => (
                        <div key={x.k} className="rv-cp-barra-fila" title={x.sub}>
                            <span>{x.k}</span>
                            <span className="rv-cp-pista-barra" aria-hidden="true">
                                <span style={{ width: `${String(destinatarios === 0 ? 0 : Math.round((x.n / destinatarios) * 100))}%` }} />
                            </span>
                            <strong>{`${cifra(x.n)}${destinatarios === 0 ? '' : ` · ${porcentaje(x.n, destinatarios)}`}`}</strong>
                        </div>
                    ))}
                </div>
            )}
            {motivos.length > 0 && (
                <div className="rv-cp-seccion">
                    <h3>Por qué no llegó</h3>
                    <ul className="rv-cp-lista">
                        {motivos.map((m) => (
                            <li key={m.clave}>
                                <span>
                                    <span>
                                        <Icono nombre={m.tipo === 'fallo' ? 'circle-alert' : 'ban'} /> {m.texto}
                                    </span>
                                </span>
                                <strong>{cifra(m.cifra)}</strong>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <div className="rv-cp-seccion">
                <h3>Reservas y compras posteriores</h3>
                <p>{detalle ? 'Reservas, citas y pedidos de quien la recibió en los 7 días siguientes. Es lo que pasó después, no una causa segura; si se cancela, se descuenta.' : 'El detalle por cliente solo lo ven el propietario y los administradores.'}</p>
                {detalle && conversiones !== null && conversiones.length > 0 && (
                    <ul className="rv-cp-lista">
                        {conversiones.map((x) => (
                            <li key={x.id}>
                                <span className={x.status === 'discounted' ? 'rv-cp-tachada' : undefined}>
                                    <strong>{nombres[x.customer_id] ?? 'Cliente'}</strong>
                                    <small>{`${VENTA[x.sale.type]} · ${diaYHora(x.occurred_at)}`}</small>
                                </span>
                                <strong className={x.status === 'discounted' ? 'rv-cp-tachada' : undefined}>{importe(x.amount)}</strong>
                            </li>
                        ))}
                    </ul>
                )}
                {detalle && mas && <small>Hay más: se ven las 5 más recientes.</small>}
            </div>
            {r !== null && <small className="rv-cp-pista">{c.status === 'sending' ? 'Se actualiza mientras se envía.' : `Actualizado ${diaYHora(r.updated_at).toLocaleLowerCase('es')}.`}</small>}
        </div>
    );
}

const VENTA: Readonly<Record<Conversion['sale']['type'], string>> = { reserva: 'Reserva', cita: 'Cita', pedido: 'Entradas' };

/** Nombres de clientes (`getCustomer` de Clientes), uno a uno y sin repetir; «Cliente» si no se puede leer. */
function useNombres(ops: PropsPantalla['ops'], negocio: string, ids: readonly string[]): Readonly<Record<string, string>> {
    const [nombres, setNombres] = useState<Readonly<Record<string, string>>>({});
    const clave = [...new Set(ids)].sort().join(',');
    useEffect(() => {
        let vivo = true;
        const faltan = clave === '' ? [] : clave.split(',');
        for (const id of faltan) {
            ops.getCustomer(negocio, id)
                .then((r) => {
                    if (vivo) {
                        setNombres((n) => ({ ...n, [id]: r.datos.display_name }));
                    }
                })
                .catch(() => undefined);
        }
        return () => {
            vivo = false;
        };
    }, [ops, negocio, clave]);
    return nombres;
}

function Destinatarios({ ops, negocio, movil, campana: c }: Pick<PropsPantalla, 'ops' | 'negocio' | 'movil'> & { campana: Campana }) {
    const [filtro, setFiltro] = useState<EstadoEnvio | ''>('');
    const pag = usePaginacionCursor();
    const [lista, setLista] = useState<{ data: Destinatario[]; next_cursor: string | null } | null>(null);
    const [error, setError] = useState<unknown>(null);
    const nombres = useNombres(ops, negocio, lista?.data.map((d) => d.customer_id) ?? []);

    useEffect(() => {
        let vivo = true;
        ops.listCampaignRecipients(negocio, c.id, { ...(filtro === '' ? {} : { status: filtro }), ...(pag.cursor === null ? {} : { cursor: pag.cursor }), limit: 20 })
            .then((r) => {
                if (vivo) {
                    setLista(r);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, c.id, filtro, pag.cursor]);

    const sinLectura = c.channel !== 'whatsapp';
    const opciones: EstadoEnvio[] = ['read', 'delivered', 'sent', 'queued', 'failed', 'discarded'];
    return (
        <div className="rv-cp-seccion">
            <p>{`Quién se dio de baja nunca se ve aquí: solo se cuenta (${cifra(c.results?.unsubscribes ?? 0)}). Los ${cifra(c.excluded_no_consent ?? 0)} sin permiso no son destinatarios.`}</p>
            <Desplegable
                etiqueta="Estado"
                valor={filtro}
                opciones={[{ valor: '', texto: 'Todos' }, ...opciones.filter((k) => !(sinLectura && k === 'read') && !(c.channel === 'telegram' && k === 'delivered')).map((k) => ({ valor: k, texto: ESTADO_DESTINATARIO[k].texto }))]}
                onChange={(v) => { setFiltro(v as EstadoEnvio | ''); pag.reiniciar(); }}
            />
            {error !== null ? (
                <EstadoError seccion="los destinatarios" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setError(null); pag.reiniciar(); }} movil={movil} />
            ) : lista === null ? (
                <EstadoCargando seccion="los destinatarios" />
            ) : (
                <>
                    <ul className="rv-cp-lista" aria-label="Destinatarios">
                        {lista.data.length === 0 && <li>No hay destinatarios con este estado.</li>}
                        {lista.data.map((d) => {
                            const l = lineaDestinatario(d);
                            const e = ESTADO_DESTINATARIO[l.estado];
                            const cuando = l.estado === 'read' ? d.read_at : l.estado === 'delivered' ? d.delivered_at : d.requested_at;
                            return (
                                <li key={d.id}>
                                    <span>
                                        <strong>{nombres[d.customer_id] ?? 'Cliente'}</strong>
                                        <small>{l.motivo ?? (l.estado === 'queued' ? 'Esperando su turno' : `${e.texto} · ${diaYHora(cuando)}`)}</small>
                                    </span>
                                    {e.chip === null ? <span>{e.texto}</span> : <Chip estado={e.chip}>{e.texto}</Chip>}
                                </li>
                            );
                        })}
                    </ul>
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={lista.next_cursor !== null} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
                </>
            )}
        </div>
    );
}
